import { TrackedObject } from 'tracked-built-ins';
import { didCancel } from 'ember-concurrency';
import { getOwner } from '@ember/application';
import { isSameConversation } from '../utils/messaging/conversation';
import { tracked } from '@glimmer/tracking';
import AddMembersContactsHandler from '../components/messaging-routes/add-members-contacts-handler.js';
import AddMembersHistoryHandler from '../components/messaging-routes/add-members-history-handler.js';
import AddMembersHistoryPage from '../components/messaging-routes/add-members-history.gjs';
import AwayMessageEditHandler from '../components/messaging-routes/away-message-edit-handler.js';
import AwayMessageEditPage from '../components/messaging-routes/away-message-edit.gjs';
import ComposeContactsHandler from '../components/messaging-routes/compose-contacts-handler.js';
import ContactsPage from '../components/messaging-routes/contacts.gjs';
import ConversationDetailsHandler from '../components/messaging-routes/conversation-details-handler.js';
import ConversationDetailsPage from '../components/messaging-routes/conversation-details.gjs';
import ConversationHandler from '../components/messaging-routes/conversation-handler.js';
import ConversationListHandler from '../components/messaging-routes/conversation-list-handler.js';
import ConversationListPage from '../components/messaging-routes/conversation-list.gjs';
import ConversationPage from '../components/messaging-routes/conversation.gjs';
import ModifyMembersContactsHandler from '../components/messaging-routes/modify-members-contacts-handler.js';
import PreviousMembersHandler from '../components/messaging-routes/previous-members-handler.js';
import PreviousMembersPage from '../components/messaging-routes/previous-members.gjs';
import Service, { service } from '@ember/service';

/**
 * Route map defining all available messaging routes with their associated
 * components and handlers. Each map key must match that route's `name` so
 * serialized history can resolve via ROUTE_MAP[routeName].
 *
 * `parent` is the screen this route belongs to. Opening the child from that
 * parent, or from anything nested under it, keeps the current screen on the
 * stack so leave returns there. Opening it from anywhere else first visits
 * the parent.
 * @type {Object}
 */
export const ROUTE_MAP = {
  awayMessageEdit: {
    name: 'awayMessageEdit',
    component: AwayMessageEditPage,
    handler: AwayMessageEditHandler,
  },
  contacts: {
    name: 'contacts',
    component: ContactsPage,
    handler: ComposeContactsHandler,
  },
  addMembers: {
    name: 'addMembers',
    component: ContactsPage,
    handler: AddMembersContactsHandler,
    parent: 'conversationDetails',
  },
  addMembersHistory: {
    name: 'addMembersHistory',
    component: AddMembersHistoryPage,
    handler: AddMembersHistoryHandler,
    parent: 'addMembers',
  },
  modifyMembers: {
    name: 'modifyMembers',
    component: ContactsPage,
    handler: ModifyMembersContactsHandler,
    // Child of the conversation, not of details. Opened from the thread
    // banner or from details; leave pops whichever of those is still live.
    parent: 'conversation',
    requiresGroupMessaging: true,
  },
  conversation: {
    name: 'conversation',
    component: ConversationPage,
    handler: ConversationHandler,
  },
  conversationList: {
    name: 'conversationList',
    component: ConversationListPage,
    handler: ConversationListHandler,
  },
  conversationDetails: {
    name: 'conversationDetails',
    component: ConversationDetailsPage,
    handler: ConversationDetailsHandler,
    parent: 'conversation',
    requiresGroupMessaging: true,
  },
  previousMembers: {
    name: 'previousMembers',
    component: PreviousMembersPage,
    handler: PreviousMembersHandler,
    parent: 'conversationDetails',
    requiresGroupMessaging: true,
  },
};

/**
 * Walk `parent` links from `routeName` up to `ancestorName`.
 *
 * Lets a child sit on the live screen that opened it whenever that screen
 * is in the child's parent tree. `modifyMembers` belongs to `conversation`,
 * so it stacks on the thread, on details, or on a later screen under
 * details. Leave then returns to that screen. No per-route allow list.
 * @param {string} routeName
 * @param {string} ancestorName
 * @returns {boolean}
 */
function isUnder(routeName, ancestorName) {
  const seen = new Set();
  let name = routeName;
  while (name && !seen.has(name)) {
    if (name === ancestorName) return true;
    seen.add(name);
    name = ROUTE_MAP[name]?.parent;
  }
  return false;
}

/** @type {number} */
export const MAX_ROUTE_HISTORY = 10;

/**
 * Manages navigation between different routes within the messaging client.
 * This service handles route state management and coordinates the lifecycle
 * of route handlers (onVisit/onLeave hooks) when navigating between routes.
 * @class MessagingNavigationService
 * @extends Service
 */
export default class MessagingNavigationService extends Service {
  @service messagingLogging;

  /**
   * Stack of active routes. The top of the stack is the current route.
   * Child routes (e.g. conversation details) are pushed so the
   * parent handler (e.g. conversation with messages) is preserved and not refetched on back.
   * @type {Object[]}
   */
  @tracked routeStack = [];

  /**
   * An array of route stacks which represent the history of navigations.
   * Used by {@link goToPreviousRoute} to restore previous route stacks.
   * @type {Array<{ routeName: string, options: Object }[]>}
   */
  @tracked historyStack = [];

  // Handlers are rebuilt on every navigation, so a handler that has been left is
  // never visited again.
  #leftHandlers = new WeakSet();

  /**
   * Whether group messaging is enabled.
   * Snapshot set by the messaging service during initialization to avoid circular dependency.
   * @type {boolean}
   */
  #groupMessagingEnabled = false;

  /**
   * The currently active route (top of the stack).
   * @type {Object|null}
   * @property {Component} component - The component to render for this route
   * @property {Object} handler - The route handler instance with onVisit/onLeave hooks
   * @property {string} name - The name of the route
   */
  get currentRoute() {
    return this.routeStack[this.routeStack.length - 1] ?? null;
  }

  /**
   * True when the live screen is the conversation thread, not a nested
   * details / add-members / previous-members route.
   */
  get isOnConversationThread() {
    return this.isCurrentRoute(ROUTE_MAP.conversation);
  }

  constructor() {
    super(...arguments);
    this.#navigateTo(ROUTE_MAP.conversationList, {}, { recordHistory: false });
  }

  setGroupMessagingEnabled(groupMessagingEnabled) {
    this.#groupMessagingEnabled = Boolean(groupMessagingEnabled);
  }

  /**
   * Checks if the provided route(s) match the current route.
   * @param {Object|Object[]} routes - A single route object or array of route objects to check
   * @returns {boolean} True if any of the provided routes match the current route
   */
  isCurrentRoute(routes) {
    if (!this.currentRoute) return false;
    if (Array.isArray(routes)) {
      return routes.some(route => route.name === this.currentRoute.name);
    }
    return routes.name === this.currentRoute.name;
  }

  async toAwayMessageEdit() {
    await this.#navigateTo(ROUTE_MAP.awayMessageEdit);
  }

  async toContacts() {
    await this.#navigateTo(ROUTE_MAP.contacts);
  }

  async toAddMembers() {
    await this.#navigateTo(ROUTE_MAP.addMembers);
  }

  async toModifyMembers() {
    await this.#navigateTo(ROUTE_MAP.modifyMembers);
  }

  async toAddMembersHistory(newMembers) {
    await this.#navigateTo(ROUTE_MAP.addMembersHistory, { newMembers });
  }

  async toConversation(conversation, { recordHistory = true } = {}) {
    await this.#navigateTo(ROUTE_MAP.conversation, { conversation }, { recordHistory });
  }

  async toConversationList({ recordHistory = true } = {}) {
    await this.#navigateTo(ROUTE_MAP.conversationList, {}, { recordHistory });
  }

  /**
   * Drops all recorded history, so Back from the next route falls back to the
   * conversation list instead of whatever the user was looking at before.
   */
  clearHistory() {
    this.historyStack = [];
  }

  async toConversationDetails() {
    await this.#navigateTo(ROUTE_MAP.conversationDetails);
  }

  async toPreviousMembers() {
    await this.#navigateTo(ROUTE_MAP.previousMembers);
  }

  /**
   * The conversation-route handler for whichever conversation is currently open
   * (thread route, or any nested child such as details or add-members),
   * regardless of which specific conversation id it holds. This is the single
   * place that understands that shape of the route stack, so websocket activity
   * handlers don't each need their own copy of that logic.
   * @type {Object|null}
   */
  get currentConversationHandler() {
    for (let i = this.routeStack.length - 1; i >= 0; i--) {
      const route = this.routeStack[i];
      if (route.name === ROUTE_MAP.conversation.name) {
        return route.handler;
      }
    }
    return null;
  }

  /**
   * True when the current route (conversation thread or nested details) is
   * showing the conversation with the given id.
   * @param {string|number} conversationId
   * @returns {boolean}
   */
  isViewingConversation(conversationId) {
    return Boolean(this.#conversationHandlerFor(conversationId));
  }

  /**
   * Replaces the conversation in the entire navigation state (active routes and history).
   * When it is open, the current screens are rebuilt with the replacement and
   * visited like any other navigation.
   * @param {string|number} conversationId
   * @param {Object} newConversation - Replacement conversation record
   * @returns {Promise<void>}
   */
  async replaceConversationInNavigationState(conversationId, newConversation) {
    const id = String(conversationId);
    const swap = serializedStack =>
      serializedStack.map(serializedRoute => {
        if (
          serializedRoute.routeName === ROUTE_MAP.conversation.name &&
          String(serializedRoute.options?.conversation?.id) === id
        ) {
          return {
            ...serializedRoute,
            options: { ...serializedRoute.options, conversation: newConversation },
          };
        }
        return serializedRoute;
      });

    this.historyStack = this.historyStack.map(swap);

    if (this.isViewingConversation(id)) {
      await this.#restoreSerializedStack(swap(this.#serializeRouteStackForHistory()));
    }
  }

  /**
   * Removes history entries that reference the given conversation so
   * goToPreviousRoute cannot restore a conversation the user left or that was
   * replaced/destroyed.
   * @param {string|number} conversationId
   */
  invalidateHistory(conversationId) {
    const id = String(conversationId);
    this.historyStack = this.historyStack.filter(
      entry => !this.#historyEntryReferencesConversation(entry, id)
    );
  }

  async goToPreviousRoute() {
    // If no history, fallback to conversation list.
    const previous = this.historyStack[this.historyStack.length - 1];
    if (!previous) {
      await this.toConversationList();
      return;
    }

    // Otherwise, restore the previous route stack.
    this.historyStack = this.historyStack.slice(0, -1);
    await this.#restoreSerializedStack(previous);
  }

  /**
   * Leaves the current child route and shows the route under it (no refetch).
   * Only has an effect when the route stack has more than one entry; otherwise returns false.
   * @returns {Promise<boolean>} True when a child was left, false when stack had only one entry
   */
  async leaveChildRoute() {
    if (this.routeStack.length <= 1) {
      return false;
    }
    const current = this.currentRoute;
    await this.#leave(current.handler);
    this.routeStack = this.routeStack.slice(0, -1);
    return true;
  }

  #historyEntryReferencesConversation(historyEntry, id) {
    return historyEntry.some(
      serializedRoute =>
        serializedRoute.routeName === ROUTE_MAP.conversation.name &&
        String(serializedRoute.options?.conversation?.id) === id
    );
  }

  /**
   * Resolves the conversation-route handler when the user is on the thread or
   * its nested details route for the given id; otherwise null.
   * @param {string|number} conversationId
   * @returns {Object|null}
   */
  #conversationHandlerFor(conversationId) {
    const id = String(conversationId);
    const handler = this.currentConversationHandler;

    if (!handler?.conversation || String(handler.conversation.id) !== id) {
      return null;
    }
    return handler;
  }

  /**
   * Navigate to `route`.
   *
   * 1. Already live (same conversation, when given): collapse descendants
   *    onto it.
   * 2. Has a parent, and the current screen is in that parent tree: push
   *    the child. Leave returns here.
   * 3. Has a parent otherwise: collapse/visit the declared parent, then
   *    push the child. Screens above the parent are left.
   * 4. No parent: replace the stack (record history unless told not to).
   * @private
   * @param {Object} route - Route definition from ROUTE_MAP
   * @param {Object} [options={}] - Options to pass to the route handler constructor
   * @param {Object} [navMeta={}] - Navigation metadata
   * @param {boolean} [navMeta.recordHistory=true] - When false, the current stack is not pushed onto historyStack before replace
   * @returns {Promise<void>} Resolves when navigation is complete
   */
  async #navigateTo(route, options = {}, { recordHistory = true } = {}) {
    if (route.requiresGroupMessaging && !this.#groupMessagingEnabled) {
      this.messagingLogging?.error(
        new Error(`Cannot navigate to route "${route.name}": group messaging is disabled`)
      );
      return;
    }

    const ancestorIndex = this.#indexOfLiveAncestor(route, options);
    if (ancestorIndex !== -1) {
      await this.#collapseTo(ancestorIndex);
      return;
    }

    if (route.parent) {
      if (this.currentRoute && isUnder(this.currentRoute.name, route.parent)) {
        await this.#pushChild(route, options);
        return;
      }

      const parentIndex = this.#indexOfLiveRoute(route.parent);
      if (parentIndex !== -1) {
        await this.#collapseTo(parentIndex);
      } else {
        const parentRoute = ROUTE_MAP[route.parent];
        if (!parentRoute) {
          throw new Error(`Unknown parent route "${route.parent}" for route "${route.name}"`);
        }
        await this.#navigateTo(parentRoute, {}, { recordHistory });
      }

      if (this.currentRoute?.name !== route.parent) return;

      await this.#pushChild(route, options);
      return;
    }

    if (recordHistory && this.routeStack.length) {
      this.#pushHistoryEntry(this.#serializeRouteStackForHistory());
    }

    for (const routeEntry of [...this.routeStack].reverse()) {
      await this.#leave(routeEntry.handler);
    }
    this.routeStack = [this.#buildCurrentRoute(route, options)];
    await this.#visit(this.currentRoute.handler);
  }

  /**
   * Visits a route. A visit cancelled because the user navigated away is
   * cleanup, not a navigation failure.
   * @private
   * @param {Object} handler
   * @returns {Promise<void>}
   */
  async #visit(handler) {
    try {
      await handler.onVisit.perform();
    } catch (error) {
      if (!didCancel(error)) throw error;
    }
  }

  /**
   * Leaves a route, cancelling its visit if it is still in flight.
   * @private
   * @param {Object} handler
   * @returns {Promise<void>}
   */
  async #leave(handler) {
    this.#leftHandlers.add(handler);
    handler.onVisit.cancelAll();
    await handler.onLeave.perform();
  }

  /**
   * Push `route` on top of the live screen. Leave pops back to it.
   * @private
   */
  async #pushChild(route, options) {
    const childOptions = { ...options, parentHandler: this.currentRoute.handler };
    const childRoute = this.#buildCurrentRoute(route, childOptions);
    this.routeStack = [...this.routeStack, childRoute];
    await this.#visit(childRoute.handler);
  }

  #indexOfLiveRoute(routeName) {
    for (let i = this.routeStack.length - 1; i >= 0; i--) {
      if (this.routeStack[i].name === routeName) return i;
    }
    return -1;
  }

  /**
   * Last index of a live route with the same name. When `options.conversation`
   * is present, the live frame's conversation id must match so navigating to a
   * different conversation replaces instead of collapsing onto the current tree.
   * @private
   * @param {Object} route
   * @param {Object} options
   * @returns {number}
   */
  #indexOfLiveAncestor(route, options) {
    const targetConversation = options.conversation;
    for (let i = this.routeStack.length - 1; i >= 0; i--) {
      const entry = this.routeStack[i];
      if (entry.name !== route.name) continue;
      if (targetConversation) {
        const liveConversation =
          entry.handler?.conversation ?? entry.handler?.options?.conversation;
        if (!isSameConversation(targetConversation, liveConversation)) {
          continue;
        }
      }
      return i;
    }
    return -1;
  }

  /**
   * Discards descendants of `routeStack[index]` in one write so intermediate
   * screens never become current. Does not visit/leave the destination or
   * touch historyStack.
   * @private
   * @param {number} index
   * @returns {Promise<void>}
   */
  async #collapseTo(index) {
    if (index === this.routeStack.length - 1) return;

    const discarded = this.routeStack.slice(index + 1);
    for (const route of [...discarded].reverse()) {
      await this.#leave(route.handler);
    }
    this.routeStack = this.routeStack.slice(0, index + 1);
  }

  /**
   * Builds a tracked route object with the component, handler instance, and route name.
   * @private
   * @param {Object} route - Route definition from ROUTE_MAP
   * @param {Object} options - Options to pass to the route handler constructor
   * @returns {TrackedObject} The constructed route object
   */
  #buildCurrentRoute(route, options) {
    return new TrackedObject({
      component: route.component,
      // eslint-disable-next-line new-cap
      handler: new route.handler(getOwner(this), options),
      name: route.name,
    });
  }

  /**
   * @param {{ routeName: string, options: Object }[]} historyEntry
   */
  #pushHistoryEntry(historyEntry) {
    const updatedStack = [...this.historyStack, historyEntry];
    while (updatedStack.length > MAX_ROUTE_HISTORY) {
      // Once we exceed the max history length, kick out the oldest entries.
      updatedStack.shift();
    }
    this.historyStack = updatedStack;
  }

  #serializeRouteStackForHistory() {
    return this.routeStack.map(route => ({
      routeName: route.name,
      options: this.#cloneOptionsForHistory(route.handler.options),
    }));
  }

  #cloneOptionsForHistory(options) {
    if (!options || typeof options !== 'object') {
      return {};
    }
    const clone = { ...options };
    // Since we rebuild the parent handler on restore, we don't want to include it in the history.
    delete clone.parentHandler;
    return clone;
  }

  /**
   * Replaces the stack with the conversation list.
   * This is used as a fallback when the history is corrupt or inconsistent.
   * @private
   */
  async #fallbackToConversationList() {
    this.routeStack = [this.#buildCurrentRoute(ROUTE_MAP.conversationList, {})];
    await this.#visit(this.currentRoute.handler);
  }

  /**
   * @param {{ routeName: string, options: Object }[]} serializedStack
   */
  async #restoreSerializedStack(serializedStack) {
    // Leave all current routes.
    for (const routeEntry of [...this.routeStack].reverse()) {
      await this.#leave(routeEntry.handler);
    }

    // Restore the previous route stack.
    const restoredStack = [];
    for (const serializedRoute of serializedStack) {
      const routeDef = ROUTE_MAP[serializedRoute.routeName];
      if (!routeDef) {
        return this.#fallbackToConversationList();
      }
      if (routeDef.requiresGroupMessaging && !this.#groupMessagingEnabled) {
        continue;
      }
      const options = { ...this.#cloneOptionsForHistory(serializedRoute.options) };
      if (routeDef.parent) {
        const parent = restoredStack[restoredStack.length - 1];
        // Same rule as live navigation: the frame below must be in the
        // child's parent tree (the parent itself, or a screen nested under it).
        if (!parent || !isUnder(parent.name, routeDef.parent)) {
          return this.#fallbackToConversationList();
        }
        options.parentHandler = parent.handler;
      }
      restoredStack.push(this.#buildCurrentRoute(routeDef, options));
    }

    if (!restoredStack.length) {
      return this.#fallbackToConversationList();
    }

    // Set the restored stack and visit each route. Stop once a navigation during
    // a visit has started leaving the remaining routes.
    this.routeStack = restoredStack;
    for (const entry of restoredStack) {
      if (this.#leftHandlers.has(entry.handler)) return;
      await this.#visit(entry.handler);
    }
  }
}
